import { z } from 'zod';

// Base config schema
const baseConfigSchema = z.object({
  id: z.string().cuid(),
  code: z.string().min(1),
  name: z.string().min(1),
  displayOrder: z.number().int().min(0),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Venue Type Config
export const venueTypeConfigSchema = baseConfigSchema.extend({
  description: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
});

export const createVenueTypeConfigSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  icon: z.string().max(50).optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  displayOrder: z.coerce.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
});

export const updateVenueTypeConfigSchema = createVenueTypeConfigSchema.partial();

// Amenity Config
export const amenityConfigSchema = baseConfigSchema.extend({
  description: z.string().optional(),
  icon: z.string().optional(),
  category: z.string().optional(),
  venueTypes: z.array(z.string()),
});

export const createAmenityConfigSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  icon: z.string().max(50).optional(),
  category: z.string().max(50).optional(),
  venueTypes: z.array(z.string()).optional().default([]),
  displayOrder: z.coerce.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
});

export const updateAmenityConfigSchema = createAmenityConfigSchema.partial();

// Inventory Category Config
export const inventoryCategoryConfigSchema = baseConfigSchema.extend({
  description: z.string().optional(),
  icon: z.string().optional(),
  venueTypes: z.array(z.string()),
});

export const createInventoryCategoryConfigSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  icon: z.string().max(50).optional(),
  venueTypes: z.array(z.string()).default([]),
  displayOrder: z.coerce.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
});

export const updateInventoryCategoryConfigSchema = createInventoryCategoryConfigSchema.partial();

// Allergen Config
export const allergenConfigSchema = baseConfigSchema.extend({
  icon: z.string().optional(),
});

export const createAllergenConfigSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  icon: z.string().max(50).optional(),
  displayOrder: z.coerce.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
});

export const updateAllergenConfigSchema = createAllergenConfigSchema.partial();

// Spice Level Config
export const spiceLevelConfigSchema = z.object({
  id: z.string().cuid(),
  level: z.number().int().min(0).max(10),
  name: z.string().min(1),
  icon: z.string().optional(),
  color: z.string().optional(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const createSpiceLevelConfigSchema = z.object({
  level: z.coerce.number().int().min(0).max(10),
  name: z.string().min(1).max(100),
  icon: z.string().max(50).optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  isActive: z.boolean().default(true),
});

export const updateSpiceLevelConfigSchema = createSpiceLevelConfigSchema.partial();

// Type exports
export type VenueTypeConfig = z.infer<typeof venueTypeConfigSchema>;
export type CreateVenueTypeConfigDto = z.infer<typeof createVenueTypeConfigSchema>;
export type UpdateVenueTypeConfigDto = z.infer<typeof updateVenueTypeConfigSchema>;

export type AmenityConfig = z.infer<typeof amenityConfigSchema>;
export type CreateAmenityConfigDto = z.infer<typeof createAmenityConfigSchema>;
export type UpdateAmenityConfigDto = z.infer<typeof updateAmenityConfigSchema>;

export type InventoryCategoryConfig = z.infer<typeof inventoryCategoryConfigSchema>;
export type CreateInventoryCategoryConfigDto = z.infer<typeof createInventoryCategoryConfigSchema>;
export type UpdateInventoryCategoryConfigDto = z.infer<typeof updateInventoryCategoryConfigSchema>;

export type AllergenConfig = z.infer<typeof allergenConfigSchema>;
export type CreateAllergenConfigDto = z.infer<typeof createAllergenConfigSchema>;
export type UpdateAllergenConfigDto = z.infer<typeof updateAllergenConfigSchema>;

export type SpiceLevelConfig = z.infer<typeof spiceLevelConfigSchema>;
export type CreateSpiceLevelConfigDto = z.infer<typeof createSpiceLevelConfigSchema>;
export type UpdateSpiceLevelConfigDto = z.infer<typeof updateSpiceLevelConfigSchema>;
